import { useRef, useState, useEffect } from "react";
import { useDrag } from "hooks";
import { Icon } from "components";
import { getPanelConfigSchema } from "../panel-config";
import { Container, Row, InfoPanel, GearButton, PanelStyles, PanelStyle, Ghost } from "./styles";

const DraggablePanel = ({
  type,
  onPointerDown,
  onDrag,
  onPointerUp,
  draggedItem,
}) => {
  const dragRef = useRef();

  useDrag(dragRef, [draggedItem], {
    onPointerDown,
    onDrag,
    onPointerUp,
  });

  return <PanelStyle ref={dragRef}>{type}</PanelStyle>;
};

export const PanelChooser = ({
  name,
  onChange,
  onConfigure,
  panels = ["Undefined", "Undefined", "Undefined", "Undefined", "Undefined"],
}) => {
  const [slot, setSlot] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dropHover, setDropHover] = useState(null);
  const [ghostPos, setGhostPos] = useState({ x: 0, y: 0 });
  const [slotTypes, setSlotTypes] = useState({});
  const ghostRef = useRef();

  const dropTargetRefs = useRef([]);

  // Helper to get panel type - handles both string arrays and object arrays
  const getPanelType = (panel) => {
    if (typeof panel === "string") return panel;
    return panel?.details?.type || panel?.type;
  };

  // Helper to get panel slot - handles both string arrays and object arrays
  const getPanelSlot = (panel, index) => {
    if (typeof panel === "string") return index;
    return panel?.details?.slot ?? index;
  };

  // Initialize slotTypes from panels prop
  useEffect(() => {
    const initialTypes = {};
    panels.forEach((panel, index) => {
      const type = getPanelType(panel);
      if (type && type !== "Undefined") {
        initialTypes[getPanelSlot(panel, index)] = type;
      }
    });
    setSlotTypes(initialTypes);
  }, [panels]);

  const isActive = (slot = 0) => {
    if (slotTypes[slot]) return true;
    // Check if any panel is assigned to this slot
    return panels.some((p, index) => {
      const panelSlot = getPanelSlot(p, index);
      const panelType = getPanelType(p);
      return panelSlot === slot && panelType && panelType !== "Undefined";
    });
  };

  const getSlotDisplay = (slotIndex) => {
    // If we have a locally set type from drag-drop, show that
    if (slotTypes[slotIndex]) {
      return slotTypes[slotIndex];
    }
    // Otherwise fall back to the original panels display
    const panelIndex = panels.findIndex(
      (p, index) => getPanelSlot(p, index) === slotIndex
    );
    if (panelIndex !== -1) {
      const type = getPanelType(panels[panelIndex]);
      if (type && type !== "Undefined") return type;
    }
    return "Empty";
  };

  const handleDrop = (targetSlot, value) => {
    if (draggedItem) {
      // Update local state to reflect the dropped panel type
      setSlotTypes((prev) => ({
        ...prev,
        [targetSlot]: draggedItem.type,
      }));
      onChange({ target: { name: targetSlot, value } });
      setDraggedItem(null);
    }
  };

  const getDropTarget = (x, y) => {
    for (let i = 0; i < dropTargetRefs.current.length; i++) {
      const target = dropTargetRefs.current[i];
      if (target) {
        const rect = target.getBoundingClientRect();
        if (
          x >= rect.left &&
          x <= rect.right &&
          y >= rect.top &&
          y <= rect.bottom
        ) {
          return i;
        }
      }
    }
    return null;
  };

  useEffect(() => {
    if (draggedItem && ghostRef.current) {
      ghostRef.current.innerHTML = "";
      ghostRef.current.appendChild(draggedItem.element);
    }
  }, [draggedItem]);

  const renderSlot = (s) => {
    const type = getSlotDisplay(s);
    return (
      <InfoPanel
        key={s}
        ref={(el) => (dropTargetRefs.current[s] = el)}
        className={`${slot === s ? "selected" : ""} ${
          isActive(s) ? "active" : ""
        } ${dropHover === s ? "drop-hover" : ""}`}
        onClick={() => setSlot(s)}
      >
        {type}
        {onConfigure && getPanelConfigSchema(type) && (
          <GearButton
            type="button"
            aria-label={`Configure ${type}`}
            onClick={(e) => {
              e.stopPropagation();
              onConfigure(s, type);
            }}
          >
            <Icon icon="cog-outline" />
          </GearButton>
        )}
      </InfoPanel>
    );
  };

  const panelTypes = ["clock", "weather", "news", "sign", "holiday", "event", "transit"];
  const panelSlots = [0, 1, 2, 3, 4];

  return (
    <Container>
      {draggedItem && (
        <Ghost ref={ghostRef} style={{ left: ghostPos.x, top: ghostPos.y }} />
      )}
      <Row>
        {panelSlots.slice(0, 3).map(renderSlot)}
      </Row>
      <Row>
        {panelSlots.slice(3, 5).map(renderSlot)}
      </Row>
      <Row>
        <PanelStyles>
          {panelTypes.map((type) => (
            <DraggablePanel
              key={type}
              type={type}
              draggedItem={draggedItem}
              onPointerDown={(e) => {
                setDraggedItem({ type, element: e.target.cloneNode(true) });
                setGhostPos({ x: e.clientX, y: e.clientY });
              }}
              onDrag={(e) => {
                setGhostPos({ x: e.clientX, y: e.clientY });
                const targetSlot = getDropTarget(e.clientX, e.clientY);
                setDropHover(targetSlot);
              }}
              onPointerUp={(e) => {
                const targetSlot = getDropTarget(e.clientX, e.clientY);
                if (targetSlot !== null) {
                  handleDrop(targetSlot, draggedItem);
                }
                setDraggedItem(null);
                setDropHover(null);
              }}
            />
          ))}
        </PanelStyles>
      </Row>
    </Container>
  );
};
export * from "./config-modal";
